import { decode as decodePng } from "fast-png";

import type { BBoxPixels, DepthInfo, ImageInfo, IntrinsicsInfo, CaptureMetadata } from "@shared/types/annotations";
import {
  inverseQuarterTurnDegrees,
  normalizeQuarterTurnDegrees,
  rotateNormalizedPoint,
} from "@shared/deferredAnalysis";
import { getStorageDownloadURL } from "@/utils/entryUtils";

/**
 * Faithful TypeScript port of the server's depth -> point-cloud -> 3D distance
 * math (glean_img_processor_deploy `measure3d.py` + the ground-truth report's
 * `nbhd3d`). It powers live recomputation of length/width while the user drags a
 * measurement endpoint, so the browser numbers stay in lock-step with the
 * server: back-project two display-frame pixels through the depth map and take
 * their (neighborhood-median) 3D distance.
 *
 * The depth map is a 16-bit grayscale PNG; it MUST be decoded losslessly with
 * `fast-png` — a `<canvas>` would truncate it to 8-bit and silently corrupt
 * every measurement.
 */

export interface RescaledIntrinsics {
  fx: number;
  fy: number;
  cx: number;
  cy: number;
}

export interface DecodedDepthPng {
  data: Uint16Array;
  width: number;
  height: number;
}

export interface DepthFrame {
  /** Depth-frame grid, row-major, in meters. Invalid pixels are NaN. */
  meters: Float32Array;
  depth: DepthInfo;
  image: ImageInfo;
  intrinsics: IntrinsicsInfo;
  /** Intrinsics pre-rescaled into the depth frame (invariant for the frame, so
   *  computed once rather than on every drag-move recompute). */
  rescaledIntrinsics: RescaledIntrinsics;
}

/** Why a capture has no usable depth frame. Only `fetch-failed` is worth
 *  retrying; every other cause is a property of the capture itself. */
export type DepthFrameFailureCause =
  | "fetch-failed"
  | "object-missing"
  | "decode-failed"
  | "missing-descriptor"
  | "misaligned"
  | "dimension-mismatch"
  | "frame-invalid";

export type DepthFrameResult = { ok: true; frame: DepthFrame } | { ok: false; cause: DepthFrameFailureCause };

type DepthMapResult = { ok: true; decoded: DecodedDepthPng } | { ok: false; cause: DepthFrameFailureCause };

/** A decoded segmentation mask (display-frame, bbox-relative) — `grid[row][col]`
 *  true where the object is, mapped over `bbox`. */
export interface MaskContext {
  grid: boolean[][];
  bbox: BBoxPixels;
}

/**
 * Scale the calibrated (portrait image frame) intrinsics into the depth frame.
 * Single height-ratio factor, matching `measure_utils.rescale_intrinsics`;
 * requires the depth grid to share the image aspect ratio.
 */
export function rescaleIntrinsics(intrinsics: IntrinsicsInfo, depth: DepthInfo): RescaledIntrinsics {
  const { fx, fy, cx, cy, referenceWidth, referenceHeight } = intrinsics;
  if (Math.abs(referenceWidth / referenceHeight - depth.width / depth.height) > 1e-3) {
    throw new Error(`Depth aspect mismatch ${referenceWidth}x${referenceHeight} vs ${depth.width}x${depth.height}`);
  }
  const s = depth.height / referenceHeight;
  return { fx: fx * s, fy: fy * s, cx: cx * s, cy: cy * s };
}

/**
 * Convert a raw depth-PNG pixel value to meters. Mirrors
 * `depthPngToGzippedFloat32`: `milli*` units divide by 1000, the invalid
 * sentinel becomes NaN.
 */
export function depthValueToMeters(raw: number, depth: DepthInfo): number {
  if (raw === depth.invalidValue) return Number.NaN;
  const units = (depth.units ?? "").toLowerCase();
  const scaleToMeters = units.startsWith("milli") ? (depth.scale ?? 1) / 1000 : (depth.scale ?? 1);
  return raw * scaleToMeters;
}

/**
 * Map a display-frame image pixel to the (rounded) nearest depth-grid pixel.
 * Applies `image.rotationToDepthFrameDegrees` first, so AR captures whose depth
 * frame is rotated relative to the saved image (e.g. portrait image + landscape
 * depth) sample the correct depth pixel — matching the server's pairing.
 */
export function imagePxToDepthPx(
  pt: { x: number; y: number },
  image: ImageInfo,
  depth: DepthInfo
): { u: number; v: number } {
  const rotation = normalizeQuarterTurnDegrees(image.rotationToDepthFrameDegrees);
  const rotated = rotateNormalizedPoint({ x: pt.x / image.width, y: pt.y / image.height }, rotation);
  return {
    u: Math.round(rotated.x * depth.width),
    v: Math.round(rotated.y * depth.height),
  };
}

/** Back-project pixel (u=col, v=row) at depth z (m) to a metric (x, y, z) point. */
export function deproject(
  u: number,
  v: number,
  z: number,
  intr: RescaledIntrinsics
): { x: number; y: number; z: number } {
  return {
    x: ((u - intr.cx) * z) / intr.fx,
    y: ((v - intr.cy) * z) / intr.fy,
    z,
  };
}

/** 3D euclidean distance between two metric points, returned in centimeters. */
export function distance3DCm(p1: { x: number; y: number; z: number }, p2: { x: number; y: number; z: number }): number {
  return Math.hypot(p1.x - p2.x, p1.y - p2.y, p1.z - p2.z) * 100;
}

function metersAt(meters: Float32Array, u: number, v: number, width: number, height: number): number {
  if (u < 0 || v < 0 || u >= width || v >= height) return Number.NaN;
  return meters[v * width + u];
}

/** Map a display point into mask-grid cell coords and test membership. */
export function isDisplayPointInMask(pt: { x: number; y: number }, grid: boolean[][], bbox: BBoxPixels): boolean {
  const maskHeight = grid.length;
  const maskWidth = grid[0]?.length ?? 0;
  if (maskHeight === 0 || maskWidth === 0 || bbox.width === 0 || bbox.height === 0) return false;
  const mx = Math.floor(((pt.x - bbox.x) / bbox.width) * maskWidth);
  const my = Math.floor(((pt.y - bbox.y) / bbox.height) * maskHeight);
  return my >= 0 && my < maskHeight && mx >= 0 && mx < maskWidth && grid[my][mx] === true;
}

/** Build a depth-frame predicate that is true where the depth pixel maps back
 *  (inverse rotation + scale) into the display-frame mask. */
function buildDepthMaskPredicate(frame: DepthFrame, mask: MaskContext): (u: number, v: number) => boolean {
  const rotation = normalizeQuarterTurnDegrees(frame.image.rotationToDepthFrameDegrees);
  const inverse = inverseQuarterTurnDegrees(rotation);
  return (u, v) => {
    const displayNorm = rotateNormalizedPoint(
      { x: (u + 0.5) / frame.depth.width, y: (v + 0.5) / frame.depth.height },
      inverse
    );
    return isDisplayPointInMask(
      { x: displayNorm.x * frame.image.width, y: displayNorm.y * frame.image.height },
      mask.grid,
      mask.bbox
    );
  };
}

function neighborhoodPoints(
  u: number,
  v: number,
  meters: Float32Array,
  width: number,
  height: number,
  intr: RescaledIntrinsics,
  radius: number,
  inMask?: (u: number, v: number) => boolean
): { x: number; y: number; z: number }[] {
  const out: { x: number; y: number; z: number }[] = [];
  for (let dv = -radius; dv <= radius; dv++) {
    for (let du = -radius; du <= radius; du++) {
      const uu = u + du;
      const vv = v + dv;
      const z = metersAt(meters, uu, vv, width, height);
      if (Number.isFinite(z) && z > 0 && (!inMask || inMask(uu, vv))) out.push(deproject(uu, vv, z, intr));
    }
  }
  return out;
}

// Sorts `values` in place — callers pass a throwaway array built per recompute.
// Matches np.median: an even count averages the two middle values.
function median(values: number[]): number {
  values.sort((a, b) => a - b);
  const mid = values.length >> 1;
  return values.length % 2 ? values[mid] : (values[mid - 1] + values[mid]) / 2;
}

/**
 * Median pairwise 3D distance (cm) between a small valid-depth patch around each
 * anchor (the server's cluster method anchored at the user's two points). Drops
 * invalid-depth pixels; returns null when either patch has no valid depth.
 */
export function neighborhoodDistanceCm(
  u1: number,
  v1: number,
  u2: number,
  v2: number,
  meters: Float32Array,
  width: number,
  height: number,
  intr: RescaledIntrinsics,
  radius = 2,
  inMask?: (u: number, v: number) => boolean
): number | null {
  // Prefer in-mask valid-depth pixels; if an endpoint's patch has no in-mask
  // depth (dragged off the object), fall back to any valid depth so the number
  // still updates — the off-mask warning is surfaced separately in the UI.
  const patch = (u: number, v: number) => {
    const masked = inMask ? neighborhoodPoints(u, v, meters, width, height, intr, radius, inMask) : [];
    return masked.length ? masked : neighborhoodPoints(u, v, meters, width, height, intr, radius);
  };
  const a = patch(Math.round(u1), Math.round(v1));
  const b = patch(Math.round(u2), Math.round(v2));
  if (!a.length || !b.length) return null;
  const distances: number[] = [];
  for (const pa of a) {
    for (const pb of b) distances.push(distance3DCm(pa, pb));
  }
  return median(distances);
}

/**
 * Recompute the 3D distance (cm) between two display-frame image-pixel
 * endpoints using the prepared depth frame. Returns null when the depth is not
 * aligned to the image or no valid depth surrounds either endpoint.
 */
export function measureDistanceCm(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  frame: DepthFrame,
  options?: { mask?: MaskContext | null; radius?: number }
): number | null {
  if (!frame.depth.alignedToImage) return null;
  const radius = options?.radius ?? 2;
  const inMask = options?.mask ? buildDepthMaskPredicate(frame, options.mask) : undefined;
  const d1 = imagePxToDepthPx(p1, frame.image, frame.depth);
  const d2 = imagePxToDepthPx(p2, frame.image, frame.depth);
  return neighborhoodDistanceCm(
    d1.u,
    d1.v,
    d2.u,
    d2.v,
    frame.meters,
    frame.depth.width,
    frame.depth.height,
    frame.rescaledIntrinsics,
    radius,
    inMask
  );
}

/** Render a meters grid to an RGBA buffer for the debug depth view:
 * nearest valid depth is red, farthest is blue, and invalid depth is black. */
export function depthToRedBlue(
  meters: Float32Array,
  width: number,
  height: number
): { data: Uint8ClampedArray; width: number; height: number } {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < meters.length; i++) {
    const z = meters[i];
    if (Number.isFinite(z) && z > 0) {
      if (z < min) min = z;
      if (z > max) max = z;
    }
  }
  const range = max > min ? max - min : 1;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < meters.length; i++) {
    const z = meters[i];
    const valid = Number.isFinite(z) && z > 0;
    const normalized = valid ? (z - min) / range : 0;
    const offset = i * 4;
    data[offset] = valid ? Math.round(255 * (1 - normalized)) : 0;
    data[offset + 1] = 0;
    data[offset + 2] = valid ? Math.round(255 * normalized) : 0;
    data[offset + 3] = 255;
  }
  return { data, width, height };
}

/** Losslessly decode a 16-bit grayscale depth PNG into one value per pixel. */
export function decodeDepthPng(buffer: Uint8Array): DecodedDepthPng {
  const png = decodePng(buffer);
  const channels = png.channels ?? 1;
  const source = png.data as ArrayLike<number>;
  if (channels === 1) {
    return { data: Uint16Array.from(source), width: png.width, height: png.height };
  }
  // Depth is grayscale; if extra channels exist, keep the first per pixel.
  const data = new Uint16Array(png.width * png.height);
  for (let i = 0; i < data.length; i++) data[i] = source[i * channels];
  return { data, width: png.width, height: png.height };
}

// Small in-memory cache so a depth PNG is fetched + decoded once per image
// while the user repeatedly drags its endpoints. Keyed by storage path (+ an
// optional version token). Bounded to avoid unbounded growth across a session.
const DEPTH_MAP_CACHE_LIMIT = 24;
const depthMapCache = new Map<string, Promise<DepthMapResult>>();

/** @internal test helper */
export function __clearDepthMapCacheForTests(): void {
  depthMapCache.clear();
  depthFrameCache.clear();
}

/**
 * Fetch + losslessly decode the depth PNG at a Storage path, keeping WHY the
 * load failed. Cached per path so live dragging only downloads/decodes once.
 */
function loadDepthMapResult(depthCloudPath: string, version?: string): Promise<DepthMapResult> {
  const cacheKey = version ? `${depthCloudPath}@${version}` : depthCloudPath;
  const cached = depthMapCache.get(cacheKey);
  if (cached) return cached;

  const promise = (async (): Promise<DepthMapResult> => {
    let buffer: ArrayBuffer;
    try {
      const url = await getStorageDownloadURL(depthCloudPath);
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`loadDepthMap: failed to fetch depth (${response.status} ${response.statusText})`);
        return { ok: false, cause: response.status === 404 ? "object-missing" : "fetch-failed" };
      }
      buffer = await response.arrayBuffer();
    } catch (error) {
      console.warn("loadDepthMap: error loading depth map", error);
      // In production a missing object rejects from getDownloadURL (with a
      // FirebaseError code) before any fetch runs — that is terminal, not
      // retryable. The emulator path has no preflight, so its 404 is caught above.
      const code = (error as { code?: string } | null)?.code;
      return { ok: false, cause: code === "storage/object-not-found" ? "object-missing" : "fetch-failed" };
    }
    try {
      return { ok: true, decoded: decodeDepthPng(new Uint8Array(buffer)) };
    } catch (error) {
      console.warn("loadDepthMap: error loading depth map", error);
      return { ok: false, cause: "decode-failed" };
    }
  })();

  // Evict failures so a transient error can retry later; bound the cache size.
  promise.then((result) => {
    if (!result.ok) depthMapCache.delete(cacheKey);
  });
  if (depthMapCache.size >= DEPTH_MAP_CACHE_LIMIT) {
    const oldest = depthMapCache.keys().next().value;
    if (oldest !== undefined) depthMapCache.delete(oldest);
  }
  depthMapCache.set(cacheKey, promise);
  return promise;
}

/**
 * Fetch + losslessly decode the depth PNG at a Storage path. Cached per path so
 * live dragging only downloads/decodes once. Returns null on any failure so
 * callers fall back to the model's measurement.
 */
export function loadDepthMap(depthCloudPath: string, version?: string): Promise<DecodedDepthPng | null> {
  return loadDepthMapResult(depthCloudPath, version).then((result) => (result.ok ? result.decoded : null));
}

/**
 * Validate + convert a decoded depth PNG into a ready-to-sample DepthFrame,
 * keeping WHY the capture cannot be measured in 3D so callers can tell an
 * unusable depth frame apart from a capture that never had depth.
 */
export function buildDepthFrameResult(decoded: DecodedDepthPng, metadata: CaptureMetadata): DepthFrameResult {
  const { depth, image, intrinsics } = metadata;
  if (!depth || !intrinsics) {
    console.warn("buildDepthFrame: capture metadata missing depth or intrinsics");
    return { ok: false, cause: "missing-descriptor" };
  }
  // A non-aligned depth can never recompute a measurement (measureDistanceCm
  // would always return null), so treat it as depth-unavailable — the UI then
  // keeps the endpoints locked instead of allowing recompute-less drags.
  if (!depth.alignedToImage) {
    console.warn("buildDepthFrame: depth is not aligned to the image");
    return { ok: false, cause: "misaligned" };
  }
  if (decoded.width !== depth.width || decoded.height !== depth.height) {
    console.warn(
      `buildDepthFrame: depth PNG ${decoded.width}x${decoded.height} does not match metadata ${depth.width}x${depth.height}`
    );
    return { ok: false, cause: "dimension-mismatch" };
  }
  let rescaledIntrinsics: RescaledIntrinsics;
  try {
    rescaledIntrinsics = rescaleIntrinsics(intrinsics, depth);
    // Validate the rotation up front so live recompute never throws per move.
    normalizeQuarterTurnDegrees(image.rotationToDepthFrameDegrees);
  } catch (error) {
    console.warn("buildDepthFrame: cannot prepare depth frame", error);
    return { ok: false, cause: "frame-invalid" };
  }
  const meters = new Float32Array(decoded.data.length);
  for (let i = 0; i < decoded.data.length; i++) meters[i] = depthValueToMeters(decoded.data[i], depth);
  return { ok: true, frame: { meters, depth, image, intrinsics, rescaledIntrinsics } };
}

/**
 * Validate + convert a decoded depth PNG into a ready-to-sample DepthFrame.
 * Returns null (with a warning) when the capture lacks depth/intrinsics or the
 * depth grid does not match the metadata, so callers can fall back to the
 * model's measurement instead of recomputing.
 */
export function buildDepthFrame(decoded: DecodedDepthPng, metadata: CaptureMetadata): DepthFrame | null {
  const result = buildDepthFrameResult(decoded, metadata);
  return result.ok ? result.frame : null;
}

// Cache the fully-built frame (not just the decoded PNG) so re-entering edit
// mode on the same capture doesn't re-allocate the meters grid. Only successful
// frames are cached, so transient load failures retry.
const depthFrameCache = new Map<string, DepthFrame>();

/**
 * Load + decode + build a ready-to-sample DepthFrame for a capture, cached by
 * storage path (+ version), keeping WHY depth is unavailable so the caller can
 * distinguish a retryable network failure from an unusable capture.
 */
export async function loadDepthFrameResult(
  depthCloudPath: string,
  metadata: CaptureMetadata,
  version?: string
): Promise<DepthFrameResult> {
  const cacheKey = version ? `${depthCloudPath}@${version}` : depthCloudPath;
  const cached = depthFrameCache.get(cacheKey);
  if (cached) return { ok: true, frame: cached };

  const loaded = await loadDepthMapResult(depthCloudPath, version);
  if (!loaded.ok) return loaded;

  const result = buildDepthFrameResult(loaded.decoded, metadata);
  if (result.ok) {
    if (depthFrameCache.size >= DEPTH_MAP_CACHE_LIMIT) {
      const oldest = depthFrameCache.keys().next().value;
      if (oldest !== undefined) depthFrameCache.delete(oldest);
    }
    depthFrameCache.set(cacheKey, result.frame);
  }
  return result;
}

/**
 * Load + decode + build a ready-to-sample DepthFrame for a capture, cached by
 * storage path (+ version). Returns null when depth is unavailable/misaligned.
 */
export async function loadDepthFrame(
  depthCloudPath: string,
  metadata: CaptureMetadata,
  version?: string
): Promise<DepthFrame | null> {
  const result = await loadDepthFrameResult(depthCloudPath, metadata, version);
  return result.ok ? result.frame : null;
}
